import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Paper, Stack } from '@mui/material'
import { selectActiveChartId } from '../../../selectors/chart'
import { AppContext, AppContextType } from '../../../App'
import { DrawingDisplayType, DrawingTypes, ChartDrawingItem } from '../drawingTools/drawing.interface'
import { ChartEvents } from '../chart.events'
import DrawingToolMenu from './drawingToolMenu/drawingToolMenu'
import { lineToolMenuItems } from './drawingToolMenu/lineToolMenuItems'
import { shapesToolMenuItems } from './drawingToolMenu/shapesToolMenuItems'
import { fibonacciToolMenuItems } from './drawingToolMenu/fibonacciTools'
import { forecastToolMenuItems } from './drawingToolMenu/forecastToolsMenuItems'
import { patternsToolMenuItems } from './drawingToolMenu/patternToolsMenu'
import { textToolsMenuItems } from './drawingToolMenu/textToolsMenu'
import MagnetIcon from '../../../assets/icons/drawingTools/magnet'
import ZoomInIcon from '../../../assets/icons/drawingTools/zoomInTool'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import { SeriesGroupCategory } from '../../../pages/home'
import DeleteIcon from '../../../assets/icons/drawingTools/deleteIcon'
import { oiProfileMenuItems } from './drawingToolMenu/oiProfileMenuItems'
import OiDrawingToolMenu from './drawingToolMenu/oiDrawingToolMenu'
import { ActionType, useUndoRedoContext } from "../../UndoRedoFunctonalities/UndoRedo.context";
import ThinArrowUpIcon from '../../../assets/icons/ThinArrowUpIcon'
import {
    DRAWING_TOOLBAR_CHEVRON_CLASS,
    DRAWING_TOOLBAR_MENU_OPEN_CLASS,
    DrawingToolbarIcon,
    drawingToolbarButtonSx,
    drawingToolbarChevronButtonSx,
    drawingToolbarMenuGroupSx,
    drawingToolbarSelectedBg,
    drawingToolbarToolSlotSx
} from './drawingToolbarShared'
import { DrawingToolbarMenuTextItem, DrawingToolbarOptionsMenu } from './drawingToolbarMenuShared'

const getUserDrawingsFromChart = (appContext: AppContextType, activeChartId: string): ChartDrawingItem[] => {
    const chart = appContext.getChart(activeChartId)
    if (!chart) return []
    return chart.drawingManager.getAllDrawings().filter(
        (drawing) => drawing.displayType === DrawingDisplayType.DRAWING
    )
}

const getIndicatorCountFromChart = (appContext: AppContextType, activeChartId: string): number => {
    const chart = appContext.getChart(activeChartId)
    if (!chart?.chartConfig?.seriesGroups) return 0
    return Object.values(chart.chartConfig.seriesGroups).filter(
        (sg) => sg.category !== SeriesGroupCategory.SERIES_SECURITY
    ).length
}

type ChartDrawingToolbarProps = {
    disable?: boolean,
    onDrawingClick: (drawingType: DrawingTypes, options: Record<string, any>, enter?: boolean) => void
}
const ChartDrawingToolbar = ({ disable = false, onDrawingClick }: ChartDrawingToolbarProps) => {
    const activeChartId = useSelector(selectActiveChartId)
    const appContext = useContext(AppContext)
    const [activeDrawing, setActiveDrawing] = useState<DrawingTypes | null>(null)
    const { push } = useUndoRedoContext()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [deleteMenuCounts, setDeleteMenuCounts] = useState<{ drawings: number; indicators: number } | null>(null)
    const chevronButtonRef = useRef<HTMLButtonElement | null>(null)
    const [magnetMode, setMagnetMode] = useState(Boolean(window.localStorage.getItem('chart_magnet_mode') || ''))
    const [zoomMode, setZoomMode] = useState(false)
    const onDrawingActive = useCallback((event: CustomEvent) => {
        setActiveDrawing(event.detail as DrawingTypes)
    }, [])

    const onDrawingInactive = useCallback(() => {
        setActiveDrawing(null)
    }, [])

    const onRemoveDrawingsClicked = useCallback(() => {
        const chart = appContext.getChart(activeChartId)
        if (!chart) return
        const userDrawings = getUserDrawingsFromChart(appContext, activeChartId)
        userDrawings.forEach((drawing) => {
            const settings = chart.drawingManager.getSettingsValues(drawing.id)
            if (settings && !settings['indicator']) {
                chart.drawingManager.removeDrawing(drawing.id, true, false)
            }
        })
        push(
            { chartId: activeChartId, drawing: userDrawings },
            "Remove Drawing From Toolbar",
            ActionType.REMOVE_DRAWING
        )
    }, [appContext, activeChartId, push])

    const onRemoveIndicators = useCallback(() => {
        appContext.getChart(activeChartId).removeAllIndicators()
    }, [activeChartId, appContext])

    useEffect(() => {
        const chart = appContext.getChart(activeChartId)
        if (chart) {
            chart.subscribeEvent(ChartEvents.DRAWING_ACTIVE, onDrawingActive)
            chart.subscribeEvent(ChartEvents.DRAWING_INACTIVE, onDrawingInactive)
        }
        return () => {
            if (chart) {
                chart.unsubscribeEvent(ChartEvents.DRAWING_ACTIVE, onDrawingActive)
                chart.unsubscribeEvent(ChartEvents.DRAWING_INACTIVE, onDrawingInactive)
            }
        }
    }, [appContext, activeChartId, onDrawingActive, onDrawingInactive])

    const onClick = useCallback((drawingType: DrawingTypes, options: Record<string, any>) => {
        if (drawingType === 'zoomIn') {
            setZoomMode(true)
        } else {
            setZoomMode(false)
        }
        if (activeDrawing !== drawingType) {
            onDrawingClick(drawingType, options, true)
        } else {
            onDrawingClick(drawingType, options, false)
        }
    }, [activeDrawing, onDrawingClick])

    const onMagnetButtonClick = useCallback(() => {
        setMagnetMode(!magnetMode)
        appContext.getChart(activeChartId).drawingManager.setMagnetMode(!magnetMode)
    }, [activeChartId, appContext, magnetMode])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
        setDeleteMenuCounts(null)
    }, [])

    const handleClick = useCallback(() => {
        if (anchorEl) {
            handleClose()
            return
        }
        setDeleteMenuCounts({
            drawings: getUserDrawingsFromChart(appContext, activeChartId).length,
            indicators: getIndicatorCountFromChart(appContext, activeChartId)
        })
        setAnchorEl(chevronButtonRef.current)
    }, [activeChartId, anchorEl, appContext, handleClose])

    useEffect(() => {
        if (activeDrawing === null) {
            setZoomMode(false)
        }
    }, [activeDrawing])

    return (
        <Paper
            id="chart-drawing-toolbar"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: '4px',
                zIndex: 10,
                height: 32,
                p: 0,
                overflow: 'visible',
                boxSizing: 'border-box'
            }}
        >
            <OiDrawingToolMenu disable={disable} items={oiProfileMenuItems} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={lineToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={shapesToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={fibonacciToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={forecastToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={patternsToolMenuItems}
                onClick={onClick} />
            <DrawingToolMenu disable={disable} activeToolId={activeDrawing} items={textToolsMenuItems} onClick={onClick}
            />
            <NonInteractiveTooltip title={magnetMode ? 'Disable Magnet Mode' : 'Enable Magnet Mode'} placement="top"
                arrow={false}>
                <span>
                    <ExtraSmallButton
                        color={magnetMode ? 'primary' : 'inherit'}
                        sx={{
                            ...drawingToolbarButtonSx,
                            bgcolor: (theme) => (magnetMode ? drawingToolbarSelectedBg(theme) : 'transparent')
                        }}
                        disabled={disable} onClick={onMagnetButtonClick}>
                        <DrawingToolbarIcon>
                            <MagnetIcon />
                        </DrawingToolbarIcon>
                    </ExtraSmallButton>
                </span>
            </NonInteractiveTooltip>
            <NonInteractiveTooltip title="Zoom In" placement="top" arrow={false}>
                <span>
                    <ExtraSmallButton
                        color={zoomMode ? 'primary' : 'inherit'} disabled={disable}
                        sx={{
                            ...drawingToolbarButtonSx,
                            bgcolor: (theme) => (zoomMode ? drawingToolbarSelectedBg(theme) : 'transparent')
                        }}
                        onClick={() => onClick('zoomIn', {})}>
                        <DrawingToolbarIcon>
                            <ZoomInIcon />
                        </DrawingToolbarIcon>
                    </ExtraSmallButton>
                </span>
            </NonInteractiveTooltip>
            <Stack alignItems={'center'} sx={drawingToolbarMenuGroupSx}>
                <Box
                    sx={drawingToolbarToolSlotSx}
                    className={anchorEl ? DRAWING_TOOLBAR_MENU_OPEN_CLASS : undefined}
                >
                    <NonInteractiveTooltip title="Remove Drawings" placement="top" arrow={false}>
                        <span>
                            <ExtraSmallButton
                                color={'inherit'}
                                disabled={disable}
                                onClick={handleClick}
                                sx={drawingToolbarButtonSx}
                            >
                                <DrawingToolbarIcon>
                                    <DeleteIcon />
                                </DrawingToolbarIcon>
                            </ExtraSmallButton>
                        </span>
                    </NonInteractiveTooltip>
                    <ExtraSmallButton
                        ref={chevronButtonRef}
                        onClick={handleClick}
                        color="inherit"
                        disabled={disable}
                        className={DRAWING_TOOLBAR_CHEVRON_CLASS}
                        sx={drawingToolbarChevronButtonSx(Boolean(anchorEl))}
                    >
                        <ThinArrowUpIcon sx={{ fontSize: '12px' }} />
                    </ExtraSmallButton>
                </Box>
                <DrawingToolbarOptionsMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    estimatedWidth={280}
                >
                    <DrawingToolbarMenuTextItem
                        label={`Remove ${deleteMenuCounts?.drawings ?? 0} drawings`}
                        onClick={() => {
                            onRemoveDrawingsClicked()
                            handleClose()
                        }}
                    />
                    <DrawingToolbarMenuTextItem
                        label={`Remove ${deleteMenuCounts?.indicators ?? 0} indicators`}
                        onClick={() => {
                            onRemoveIndicators()
                            handleClose()
                        }}
                    />
                    <DrawingToolbarMenuTextItem
                        label={`Remove ${deleteMenuCounts?.drawings ?? 0} drawings and ${deleteMenuCounts?.indicators ?? 0} indicators`}
                        onClick={() => {
                            onRemoveDrawingsClicked()
                            onRemoveIndicators()
                            handleClose()
                        }}
                    />
                </DrawingToolbarOptionsMenu>
            </Stack>
        </Paper>
    )
}

export default memo(ChartDrawingToolbar)
